import type { CrmType, TourOperatorRow } from '@ops/entities/operation-item';
import type { TourOperatorFormValues } from '../model/schema';
import {
  useCreateTourOperator,
  useRemoveTourOperator,
  useSearchSuppliers,
  useUpdateTourOperator,
} from '@ops/entities/operation-item';
import {
  backendMessageOr,
  toastMutation,
} from '@ops/shared/lib/toast-mutation';
import { Star } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { joinCss } from '@/shared/lib/css-utils';
import {
  Button,
  Input,
  InputGroup,
  Modal,
  ModalFooter,
  ModalHeader,
  PhoneInput,
  Select,
} from '@/shared/ui';
import {
  defaultTourOperatorFormValues,
  tourOperatorFormSchema,
} from '../model/schema';
import css from './tour-operator-form.module.scss';

type TourOperatorFormProps = {
  open: boolean;
  initial?: TourOperatorRow | null;
  onClose: () => void;
};

const typeOptions = [
  { value: 'Driver', label: 'Driver' },
  { value: 'Guide', label: 'Guide' },
];

const receiveMessageOptions = [
  { value: 'allowed', label: 'Allowed' },
  { value: 'only_preparation', label: 'Only preparation' },
  { value: 'not_allowed', label: 'Not allowed' },
];

const RATING_STARS = [1, 2, 3, 4, 5] as const;

const buildInitialValues = (
  initial?: TourOperatorRow | null
): Partial<TourOperatorFormValues> => {
  if (!initial) return defaultTourOperatorFormValues;
  return {
    fullName: initial.fullName,
    type: initial.type,
    receiveMessage: initial.receiveMessage,
    companyId: initial.companyId,
    phone: initial.phone ?? '',
    email: initial.email ?? '',
    city: initial.city ?? '',
    rating: initial.rating ? Number(initial.rating) : undefined,
  };
};

export const TourOperatorForm = ({
  open,
  initial,
  onClose,
}: TourOperatorFormProps) => {
  const [values, setValues] = useState<Partial<TourOperatorFormValues>>(
    buildInitialValues(initial)
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [companySearch, setCompanySearch] = useState('');
  const [showCompanyResults, setShowCompanyResults] = useState(false);

  const createTourOperator = useCreateTourOperator();
  const updateTourOperator = useUpdateTourOperator();
  const removeTourOperator = useRemoveTourOperator();

  const companyType: CrmType = '1';
  const companyQuery = useSearchSuppliers(
    { crmType: companyType, name: companySearch },
    { enabled: open && companySearch.length > 0 }
  );

  useEffect(() => {
    if (open) {
      setValues(buildInitialValues(initial));
      setErrors({});
      setCompanySearch('');
      setShowCompanyResults(false);
    }
  }, [initial, open]);

  const isEdit = !!initial;
  const submitting =
    createTourOperator.isPending ||
    updateTourOperator.isPending ||
    removeTourOperator.isPending;

  const handleField = <K extends keyof TourOperatorFormValues>(
    key: K,
    value: TourOperatorFormValues[K]
  ) => {
    setValues((current) => ({ ...current, [key]: value }));
  };

  const handleSubmit = () => {
    const ratingNumber =
      typeof values.rating === 'number' && !Number.isNaN(values.rating)
        ? values.rating
        : undefined;

    const candidate = {
      ...values,
      rating: ratingNumber,
      email: values.email || undefined,
      phone: values.phone || undefined,
      city: values.city || undefined,
    };

    const parsed = tourOperatorFormSchema.safeParse(candidate);
    if (!parsed.success) {
      const fieldErrors: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const path = issue.path[0];
        if (typeof path === 'string') fieldErrors[path] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }

    setErrors({});

    if (isEdit && initial) {
      updateTourOperator.mutate(
        { id: initial.id, ...parsed.data },
        toastMutation(
          {
            success: 'Tour operator updated',
            error: backendMessageOr('Couldn’t save changes'),
          },
          { onSuccess: () => onClose() }
        )
      );
    } else {
      createTourOperator.mutate(
        parsed.data,
        toastMutation(
          {
            success: 'Tour operator added',
            error: backendMessageOr('Couldn’t add tour operator'),
          },
          { onSuccess: () => onClose() }
        )
      );
    }
  };

  const handleDelete = () => {
    if (!initial) return;
    removeTourOperator.mutate(
      { id: initial.id },
      toastMutation(
        {
          success: 'Tour operator removed',
          error: backendMessageOr('Couldn’t remove tour operator'),
        },
        { onSuccess: () => onClose() }
      )
    );
  };

  const matches = companyQuery.data ?? [];
  const selectedCompanyName = useMemo(() => {
    if (!values.companyId) return '';
    const match = matches.find((m) => m.id === values.companyId);
    return match?.name ?? '';
  }, [matches, values.companyId]);

  return (
    <Modal onClose={onClose} open={open}>
      <ModalHeader
        onClose={onClose}
        title={isEdit ? 'Update operator' : 'Add operator'}
      />
      {/* Field set + order mirrors legacy's TourOperatorsForm (Update Driver):
          Full Name · Phone · Email · Account · City · Type · Receive Message ·
          star rating. */}
      <div className={css.root}>
        <InputGroup
          error={errors.fullName}
          htmlFor="tof-fullname"
          label="Full name"
        >
          <Input
            id="tof-fullname"
            onChange={(e) => handleField('fullName', e.target.value)}
            value={values.fullName ?? ''}
          />
        </InputGroup>

        <InputGroup error={errors.phone} htmlFor="tof-phone" label="Phone">
          {/* Country-aware, like the CRM form: it composes `+<cc><digits>` so a
              bare national number cannot be produced here in the first place. */}
          <PhoneInput
            id="tof-phone"
            name="phone"
            onChange={(value) => handleField('phone', value)}
            placeholder="Enter phone number"
            value={values.phone ?? ''}
          />
        </InputGroup>

        <InputGroup error={errors.email} htmlFor="tof-email" label="Email">
          <Input
            id="tof-email"
            onChange={(e) => handleField('email', e.target.value)}
            value={values.email ?? ''}
          />
        </InputGroup>

        <InputGroup
          error={errors.companyId}
          htmlFor="tof-company"
          label="Account"
        >
          <Input
            aria-label="Search accounts"
            id="tof-company"
            onChange={(e) => {
              setCompanySearch(e.target.value);
              setShowCompanyResults(true);
            }}
            placeholder="Type to search…"
            value={
              showCompanyResults
                ? companySearch
                : selectedCompanyName || companySearch
            }
          />
          {showCompanyResults && companySearch.length > 0 && (
            <ul className={css.suggestions}>
              {companyQuery.isLoading && (
                <li className={css.suggestionEmpty}>Searching…</li>
              )}
              {!companyQuery.isLoading && matches.length === 0 && (
                <li className={css.suggestionEmpty}>No matches</li>
              )}
              {matches.map((row) => (
                <li className={css.suggestionItem} key={row.id}>
                  <Button
                    onClick={() => {
                      handleField('companyId', row.id);
                      setCompanySearch(row.name);
                      setShowCompanyResults(false);
                    }}
                    size="sm"
                    type="button"
                    variant="ghost"
                  >
                    {row.name || '(unnamed)'}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </InputGroup>

        <InputGroup htmlFor="tof-city" label="City">
          <Input
            id="tof-city"
            onChange={(e) => handleField('city', e.target.value)}
            value={values.city ?? ''}
          />
        </InputGroup>

        <InputGroup htmlFor="tof-type" label="Type">
          <Select
            id="tof-type"
            onChange={(e) =>
              handleField('type', e.target.value as 'Driver' | 'Guide')
            }
            options={typeOptions}
            value={values.type ?? 'Driver'}
          />
        </InputGroup>

        <InputGroup htmlFor="tof-rm" label="Receive message">
          <Select
            id="tof-rm"
            onChange={(e) =>
              handleField(
                'receiveMessage',
                e.target.value as TourOperatorFormValues['receiveMessage']
              )
            }
            options={receiveMessageOptions}
            value={values.receiveMessage ?? 'allowed'}
          />
        </InputGroup>

        <InputGroup error={errors.rating} label="Rate the supplier">
          <div className={css.stars}>
            {RATING_STARS.map((n) => (
              <Button
                aria-label={`${n} star${n > 1 ? 's' : ''}`}
                aria-pressed={(values.rating ?? 0) >= n}
                key={n}
                onClick={() => handleField('rating', n)}
                size="iconSm"
                type="button"
                variant="ghost"
              >
                <Star
                  className={joinCss(
                    css.starIcon,
                    (values.rating ?? 0) >= n && css.starIconIsFilled
                  )}
                />
              </Button>
            ))}
          </div>
          {values.rating ? (
            <span className={css.ratingCurrent}>
              Current Rating: {values.rating}
            </span>
          ) : null}
        </InputGroup>
      </div>
      <ModalFooter>
        {isEdit && (
          <Button
            disabled={submitting}
            onClick={handleDelete}
            type="button"
            variant="destructive"
          >
            Delete
          </Button>
        )}
        <Button onClick={onClose} type="button" variant="outline">
          Cancel
        </Button>
        <Button
          disabled={submitting}
          onClick={handleSubmit}
          type="button"
          variant="primary"
        >
          {submitting ? 'Saving…' : isEdit ? 'Update' : 'Create'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
